(() => {
  const initShop = (root) => {
    if (!root || root.dataset.btsInitialized === 'true') return;
    root.dataset.btsInitialized = 'true';

    const grid = root.querySelector('[data-bts-grid]');
    const showMore = root.querySelector('[data-bts-show-more]');
    const empty = root.querySelector('[data-bts-empty]');
    const loading = root.querySelector('[data-bts-loading]');
    const count = root.querySelector('[data-bts-count]');
    const mobileCount = root.querySelector('[data-bts-mobile-count]');
    const dialog = root.querySelector('[data-bts-dialog]');
    const mobile = window.matchMedia('(max-width: 749px)');

    const defaults = {
      gender: 'all',
      style: root.dataset.initialStyle || 'all',
      character: 'all',
      terms: []
    };
    const state = { ...defaults, terms: [] };

    const step = () => Number(mobile.matches ? root.dataset.mobileStep : root.dataset.desktopStep) || (mobile.matches ? 12 : 20);
    let visibleLimit = step();
    let nextPageUrl = root.dataset.nextPage || '';
    let fetching = false;

    const cards = () => Array.from(grid?.querySelectorAll('[data-bts-card]') || []);
    const cardsInDisplayOrder = () => {
      const current = cards();
      if (state.style !== 'all') return current;
      return current
        .map((card, index) => ({ card, index, priority: card.dataset.style === 'freesip' ? 0 : 1 }))
        .sort((a, b) => a.priority - b.priority || a.index - b.index)
        .map((item) => item.card);
    };
    const normalizeTerms = (value) => (value || '').split(',').map((term) => term.trim().toLowerCase()).filter(Boolean);
    const genderMatches = (card, gender = state.gender) => gender === 'all' || (card.dataset.gender || '').split(/\s+/).includes(gender);
    const styleMatches = (card, style = state.style) => style === 'all' || (card.dataset.style || '').split(/\s+/).includes(style);
    const termsMatch = (card, terms = state.terms) => !terms.length || terms.some((term) => (card.dataset.search || '').includes(term));
    const matches = (card) => genderMatches(card) && styleMatches(card) && (state.character === 'all' || termsMatch(card));

    const characterButtons = () => Array.from(root.querySelectorAll('[data-character-filter]'));

    const selectedCharacterIsAvailable = () => {
      if (state.character === 'all') return true;
      const button = characterButtons().find((item) => item.dataset.characterFilter === state.character);
      return Boolean(button && !button.hidden && !button.disabled);
    };

    const updateCharacterAvailability = () => {
      const grouped = new Map();
      characterButtons().forEach((button) => {
        const id = button.dataset.characterFilter;
        if (!grouped.has(id)) grouped.set(id, []);
        grouped.get(id).push(button);
      });

      grouped.forEach((buttons, id) => {
        const first = buttons[0];
        const characterGender = first.dataset.characterGender || 'both';
        const compatible = id === 'all' || state.gender === 'all' || characterGender === 'both' || characterGender === state.gender;
        const terms = normalizeTerms(first.dataset.terms);
        const total = cards().filter((card) => genderMatches(card) && styleMatches(card) && (id === 'all' || termsMatch(card, terms))).length;
        const available = id === 'all' || (compatible && (total > 0 || Boolean(nextPageUrl)));

        buttons.forEach((button) => {
          button.hidden = !available;
          button.disabled = !available;
          button.setAttribute('aria-disabled', String(!available));
          button.querySelectorAll('[data-character-count]').forEach((node) => {
            node.textContent = String(total) + (nextPageUrl ? '+' : '');
          });
        });
      });

      if (!selectedCharacterIsAvailable()) {
        state.character = 'all';
        state.terms = [];
      }
    };

    const updatePressed = () => {
      root.querySelectorAll('[data-filter-group]').forEach((button) => {
        const active = state[button.dataset.filterGroup] === button.dataset.filterValue;
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('is-active', active);
      });

      characterButtons().forEach((button) => {
        const active = state.character === button.dataset.characterFilter;
        button.setAttribute('aria-pressed', String(active));
        button.classList.toggle('is-active', active);
      });

      const filtersActive = state.gender !== defaults.gender || state.style !== defaults.style || state.character !== defaults.character;
      root.querySelectorAll('[data-filter-reset]').forEach((button) => {
        button.disabled = !filtersActive;
        button.setAttribute('aria-disabled', String(!filtersActive));
      });
    };

    const updateCounts = (total) => {
      const word = total === 1 ? root.dataset.designSingular : root.dataset.designPlural;
      const suffix = nextPageUrl ? '+' : '';
      const label = String(total) + suffix + ' ' + word;
      if (count) count.textContent = label;
      if (mobileCount) mobileCount.textContent = label;
    };

    const apply = () => {
      const ordered = cardsInDisplayOrder();
      if (state.style === 'all') ordered.forEach((card) => grid?.appendChild(card));
      updateCharacterAvailability();
      const filtered = ordered.filter(matches);
      ordered.forEach((card) => { card.hidden = true; });
      filtered.slice(0, visibleLimit).forEach((card) => { card.hidden = false; });
      updateCounts(filtered.length);
      empty?.classList.toggle('is-visible', filtered.length === 0 && !nextPageUrl && !fetching);
      if (showMore) {
        showMore.hidden = filtered.length <= visibleLimit && !nextPageUrl;
        showMore.disabled = fetching;
      }
      updatePressed();
      root.classList.add('is-ready');
      return filtered.length;
    };

    const loadNextPage = async () => {
      if (!nextPageUrl || fetching) return false;
      fetching = true;
      loading?.classList.add('is-visible');
      if (showMore) showMore.disabled = true;

      try {
        const url = new URL(nextPageUrl, window.location.origin);
        url.searchParams.set('section_id', root.dataset.sectionId);
        const response = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('Request failed: ' + response.status);
        const html = await response.text();
        const fragment = new DOMParser().parseFromString(html, 'text/html');
        const incomingRoot = fragment.querySelector('[data-bts-section="' + root.dataset.sectionId + '"]');
        if (!incomingRoot) throw new Error('Section response was empty');

        const existing = new Set(cards().map((card) => card.dataset.handle));
        incomingRoot.querySelectorAll('[data-bts-card]').forEach((card) => {
          if (!existing.has(card.dataset.handle)) grid?.appendChild(card);
        });

        nextPageUrl = incomingRoot.dataset.nextPage || '';
        root.dataset.nextPage = nextPageUrl;
        return true;
      } catch (error) {
        console.warn('[Fann Bliss BTS] Could not load more products.', error);
        nextPageUrl = '';
        root.dataset.nextPage = '';
        return false;
      } finally {
        fetching = false;
        loading?.classList.remove('is-visible');
        if (showMore) showMore.disabled = false;
        apply();
      }
    };

    const ensureFilteredResults = async () => {
      let filteredCount = cards().filter(matches).length;
      while (nextPageUrl && filteredCount < step()) {
        const loaded = await loadNextPage();
        if (!loaded) break;
        filteredCount = cards().filter(matches).length;
      }
      apply();
    };

    const openCharacterDialog = () => dialog?.showModal();

    const setMobileFiltersCollapsed = (collapsed) => {
      root.classList.toggle('is-mobile-filters-collapsed', collapsed);
      root.querySelectorAll('[data-mobile-filter-toggle]').forEach((button) => {
        button.setAttribute('aria-expanded', String(!collapsed));
        const label = button.querySelector('[data-mobile-filter-toggle-label]');
        if (label) label.textContent = collapsed ? button.dataset.expandLabel : button.dataset.collapseLabel;
      });
    };

    const resetFilters = async () => {
      state.gender = defaults.gender;
      state.style = defaults.style;
      state.character = defaults.character;
      state.terms = [];
      visibleLimit = step();
      if (dialog?.open) dialog.close();
      apply();
      await ensureFilteredResults();
    };

    root.addEventListener('click', async (event) => {
      if (event.target.closest('[data-filter-reset]')) {
        await resetFilters();
        return;
      }

      if (event.target.closest('[data-mobile-filter-toggle]')) {
        setMobileFiltersCollapsed(!root.classList.contains('is-mobile-filters-collapsed'));
        return;
      }

      const filter = event.target.closest('[data-filter-group]');
      if (filter) {
        const group = filter.dataset.filterGroup;
        if (group && Object.prototype.hasOwnProperty.call(state, group)) {
          state[group] = filter.dataset.filterValue;
          visibleLimit = step();
          apply();
          await ensureFilteredResults();
        }
        return;
      }

      const character = event.target.closest('[data-character-filter]');
      if (character && !character.disabled) {
        state.character = character.dataset.characterFilter;
        state.terms = normalizeTerms(character.dataset.terms);
        const characterGender = character.dataset.characterGender;
        if (root.dataset.autoSelectGender === 'true' && (characterGender === 'boys' || characterGender === 'girls')) {
          state.gender = characterGender;
        }
        visibleLimit = step();
        if (dialog?.open) dialog.close();
        apply();
        await ensureFilteredResults();
        return;
      }

      if (event.target.closest('[data-bts-show-more]')) {
        visibleLimit += step();
        if (cards().filter(matches).length < visibleLimit && nextPageUrl) await loadNextPage();
        apply();
        return;
      }

      if (event.target.closest('[data-character-open]')) {
        openCharacterDialog();
        return;
      }

      if (event.target.closest('[data-character-close]')) dialog?.close();
    });

    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    mobile.addEventListener?.('change', () => {
      visibleLimit = Math.max(visibleLimit, step());
      apply();
    });

    apply();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-bts-shop]').forEach(initShop);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
